import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { OnboardingStepResponse, OnboardingStep } from '../models';
import { DeviceIdService } from '../../../core/services/device-id.service';

export interface AppConfig {
  max_images?: number;
  max_menu_processing_image_size?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private http = inject(HttpClient);
  private deviceIdService = inject(DeviceIdService);
  readonly baseUrl = environment.apiBaseUrl;
  private readonly v2BaseUrl = environment.apiBaseUrl.replace('/v3', '/v2');
  private readonly ACCESS_TOKEN_KEY = 'feather_access_token';
  private readonly DEVICE_REGISTERED_KEY = 'device_registered';

  /**
   * Login/Register device on backend using v2 API
   * This is the main login function that returns the access token
   */
  getAccessToken(): Observable<string> {
    const deviceId = this.deviceIdService.getDeviceId();
    
    return this.http.post<{ token: string }>(
      `${this.v2BaseUrl}/device/login`,
      {
        guid: deviceId,
        os_type: 3,
        is_extension: 0
      }
    ).pipe(
      map(response => {
        if (!response.token) {
          throw new Error('No token returned from server');
        }
        // Store token in localStorage
        localStorage.setItem(this.ACCESS_TOKEN_KEY, response.token);
        // Mark device as registered/logged in
        localStorage.setItem(this.DEVICE_REGISTERED_KEY, 'true');
        return response.token;
      })
    );
  }

  /**
   * Get stored access token
   */
  getStoredAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Clear stored access token
   */
  clearAccessToken(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get current onboarding step and shop data.
   * Returns null if 404: no shop for this device → start flow from step 0 (create a new shop).
   * On success, automatically updates localStorage with step and shop data.
   */
  getOnboardingStep(): Observable<OnboardingStepResponse | null> {
    const deviceId = this.deviceIdService.getDeviceId();
    const token = this.getStoredAccessToken();

    if (!token) {
      return throwError(() => new Error('No access token available'));
    }

    const params = new HttpParams()
      .set('device_id', deviceId);

    return this.http.get<any>(
      `${this.baseUrl}/shop/onboardingStatus`,
      { params, headers: { Authorization: `Bearer ${token}` } }
    ).pipe(
      map(response => {
        // Backend returns shop_onboarding_status_id at top level, not step
        // Map it to the expected OnboardingStepResponse format
        if (response && response.shop && typeof response.shop_onboarding_status_id !== 'undefined') {
          const mappedResponse: OnboardingStepResponse = {
            step: response.shop_onboarding_status_id,
            shop: response.shop
          };
          
          // Update localStorage with step and shop data
          localStorage.setItem('onboarding_current_step', String(mappedResponse.step));
          localStorage.setItem('onboarding_shop_data', JSON.stringify(mappedResponse.shop));
          
          return mappedResponse;
        }

        // If the shape is not as expected, treat as no onboarding yet
        return null;
      }),
      catchError(error => {
        // 404 = no shop for this device → start flow from 0 (create one)
        // This is expected business logic, not an error to show to the user
        if (error.status === 404) {
          localStorage.removeItem('onboarding_current_step');
          localStorage.removeItem('onboarding_shop_data');
          return of(null);
        }
        // For other errors, throw so they can be handled by the interceptor/guard
        return throwError(() => error);
      })
    );
  }

  /**
   * Get app configuration
   */
  getAppConfig(): Observable<AppConfig> {
    const token = this.getStoredAccessToken();

    if (!token) {
      return throwError(() => new Error('No access token available'));
    }

    return this.http.get<AppConfig>(
      `${this.baseUrl}/config/app`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }
}
